import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom'
import useAnimationFrame from './useAnimationFrame'
import { scaleLinear } from 'd3-scale'
import colorFn from "./colorFn"

const colorTransitionGenerator = (startColor, endColor, start, end) => {
  return scaleLinear()
    .domain([0, start, end])
    .range([startColor, startColor, endColor])
    .clamp(true)
}

const infectedColor = colorFn("INFECTED")
const susceptibleColor = colorFn("SUSCEPTIBLE")
const compromisedColor = colorFn("IMMUNO")

const Arrow = ({ x1, y1, x2, y2 }) => {
  const theta = Math.atan2(y2 - y1, x2 - x1);
  const deg = theta / Math.PI * 180
  const length = Math.hypot(x2 - x1, y2 - y1)

  return <g transform={`translate(${x1},${y1}) rotate(${deg})`}>
    <polyline 
      points={`0,0 ${length - 6},-3 ${length - 6},-6 ${length},0 ${length - 6},6 ${length - 6},3`}
      fill="#555"
    />
  </g>
}

const ScrollyNetwork = () => {
  const svgRef = useRef()
  const width = 600;
  const height = 500;

  const [renderedHeight, setRenderedHeight] = useState(height)

  const [scrollOffset, setScrollOffset] = useState(0)

  useAnimationFrame(() => {
    const svgRect = svgRef.current.getBoundingClientRect()
    setRenderedHeight(svgRect.height)
    setScrollOffset(svgRect.top)
  })

  const progressScale = scaleLinear()
    .domain([window.innerHeight - renderedHeight, window.innerHeight * 0.15])

  const progress = Math.max(0, progressScale(scrollOffset))

  const scaleScale = scaleLinear()
    .domain([0, 0.3, 0.85, 1])
    .range([1.5, 1.5, 0.9, 0.9])

  const stage1AnnotationOpacity = scaleLinear()
    .domain([0, 0.3, 0.4, 1])
    .range([1, 1, 0, 0])

  const stage2AnnotationOpacity = scaleLinear()
    .domain([0, 0.5, 0.6, 1])
    .range([0, 0, 1, 1])
  
  const stage1ColorTransition = colorTransitionGenerator(susceptibleColor, infectedColor, 0.01, 0.2)
  const stage2ColorTransition = colorTransitionGenerator(susceptibleColor, infectedColor, 0.3, 0.5)
  const stage3ColorTransition = colorTransitionGenerator(susceptibleColor, infectedColor, 0.95, 1.1)
  const stage3ComproTransition = colorTransitionGenerator(compromisedColor, infectedColor, 0.95, 1.1)

  return ReactDOM.createPortal(
    <svg ref={svgRef} style={{ width: "100%", borderBottom: "1px solid #eee", overflow: "visible" }} viewBox={`0 0 600 ${height}`}>
      <g transform={`translate(${width / 2}, ${height / 2}) scale(${scaleScale(progress)})`}>
        <g>
          <g transform="scale(0.3) translate(-50,-160) rotate(7)">
            <polyline 
              points="0,0 0,80 40,80 55,95 70,80 110,80 110,0"
            />
            <text textAnchor="middle" x="55" y="28" style={{ fontSize: "21px", fontWeight: "bold", letterSpacing: "0.2em", fill: "white" }}>LOCAL</text>
            <text textAnchor="middle" x="55" y="65" style={{ fontSize: "42px", fill: "white" }}>PARK</text>
          </g>
          <g transform={"translate(7, -17)"}><use xlinkHref="#adult" fill={stage1ColorTransition(progress)} /></g>
          <g transform={"translate(-18, -15)"}><use xlinkHref="#adult" fill={stage1ColorTransition(progress)} /></g>
          <g transform={"translate(-6, 0)"}><use xlinkHref="#adult" fill={infectedColor} /></g>
          <g>
            <Arrow x1={17} y1={-12} x2={60} y2={-48} />
            <g transform="translate(60,-48)">
              <g transform="translate(4,-13)"><use xlinkHref="#adult" fill={stage1ColorTransition(progress)} /></g>
              <g transform="translate(18,-13)"><use xlinkHref="#adult" fill={stage2ColorTransition(progress)} /></g>

              <g className="cartoon-annotations" transform="translate(0,-30)" opacity={stage1AnnotationOpacity(progress)}>
                <text x="15" y="50">&hellip;to her husband.</text>

                <g transform="translate(35, 5)">
                  <text className="personal-annotation" y="-12">27 y.o., Cashier</text>
                  <text className="personal-annotation">Healthy</text>
                  <line x1= "-1" y1="1" x2="-10" y2="10" className="personal-annotation-line" />
                </g>
              </g>

              <g className="the-grocer" opacity={stage2AnnotationOpacity(progress)}>
                <Arrow x1={27} y1={-15} x2={40} y2={-58} />

                <g transform="translate(47,-5) scale(1.5)">
                  <text>&hellip;to the grocer's</text>
                  <text y="15">customers.</text>
                </g>

                <g transform={`translate(40,-80)`}>
                  <use xlinkHref="#adult" fill={stage2ColorTransition(progress)} />

                  <use xlinkHref="#adult" y="-57" x="-75" fill={stage3ColorTransition(progress)} />
                  <use xlinkHref="#elder" y="-46" x="-60" fill={stage3ColorTransition(progress)} />
                  <use xlinkHref="#adult" y="-38" x="-45" fill={stage3ColorTransition(progress)} />
                  <use xlinkHref="#adult" y="-31" x="-30" fill={stage3ColorTransition(progress)} />
                  <use xlinkHref="#adult" y="-28" x="-15" fill={stage3ColorTransition(progress)} />
                  <use xlinkHref="#adult" y="-25" x="0" fill={stage3ColorTransition(progress)} />
                  <use xlinkHref="#adult" y="-28" x="23" fill={stage3ColorTransition(progress)} />
                  <use xlinkHref="#elder" y="-31" x="49" fill={stage3ComproTransition(progress)} />

                  <g className="the-retiree" transform="translate(70, -44)">
                    <g transform="scale(1.5)">
                      <text className="personal-annotation" y="-24">68 y.o., Retiree</text>
                      <text className="personal-annotation" y="-12">Fighting Cancer</text>
                      <text className="personal-annotation immunocompromised-text">Immunocompromised</text>
                      <line x1= "-1" y1="1" x2="-8" y2="8" className="personal-annotation-line" />
                    </g>
                  </g>

                  <g className="the-child">
                    <use xlinkHref="#child" x="-151"  y="-65" fill={stage3ComproTransition(progress)} />

                    <line x1="-75" y1="-49" x2="-139" y2="-56" stroke="#aaa" strokeDasharray="2,1" />

                    <g transform="translate(-128, -78) scale(1.5)">
                      <text className="personal-annotation" y="-36">8 y.o., Child</text>
                      <text className="personal-annotation" y="-24">Waiting for Mom</text>
                      <text className="personal-annotation" y="-12">Cystic Fibrosis</text>
                      <text className="personal-annotation immunocompromised-text">Immunocompromised</text>
                      <line x1= "-8" y1="10" x2="0" y2="2" className="personal-annotation-line" />
                    </g>
                  </g>

                  <g transform="scale(0.3) translate(90,20) rotate(-2)">
                    <polyline 
                      points="-40,0 -40,80 40,80 55,95 70,80 150,80 150,0"
                    />
                    <text textAnchor="middle" x="55" y="28" style={{ fontSize: "21px", fontWeight: "bold", letterSpacing: "0.2em", fill: "white" }}>LOCAL</text>
                    <text textAnchor="middle" x="55" y="65" style={{ fontSize: "42px", fill: "white" }}>GROCER</text>
                  </g>
                </g>
              </g>
            </g>
          </g>
          <g>
            <Arrow x1={-19} y1={-16} x2={-55} y2={-56} />
            <g transform={`translate(-68,-69)`}>
              <use xlinkHref="#adult" fill={stage1ColorTransition(progress)} />

              <g transform={`translate(-7, -21)`}>
                <use xlinkHref="#adult" fill={stage2ColorTransition(progress)} />
              </g>
              <g transform={`translate(-14, -3)`}>
                <use xlinkHref="#adult" fill={stage2ColorTransition(progress)} />
              </g>

              <g className="cartoon-annotations" opacity={stage1AnnotationOpacity(progress)}>
                <text x="10" y="35" textAnchor="end">&hellip;to his roommates.</text>

                <g transform="translate(-21, -12)">
                  <text className="personal-annotation" y="-12" textAnchor="end">32 y.o., ER Nurse</text>
                  <text className="personal-annotation" textAnchor="end">Healthy</text>
                  <line x1= "1" y1="1" x2="10" y2="10" className="personal-annotation-line" />
                </g>
              </g>

              <g className="the-er" opacity={stage2AnnotationOpacity(progress)}>
                <Arrow x1={-19} y1={7} x2={-50} y2={7} />

                <g transform="translate(-160,45) scale(1.5)">
                  <text>&hellip;to patients</text>
                  <text y="15">in the E.R.</text>
                </g>

                <g transform="translate(-64,-2)">
                  <use xlinkHref="#adult" fill={stage2ColorTransition(progress)} />

                  <g transform="scale(0.3) translate(-120,-150) rotate(12)">
                    <polyline 
                      points="10,0 10,80 40,80 55,95 70,80 100,80 100,0"
                    />
                    <text textAnchor="middle" x="55" y="28" style={{ fontSize: "21px", fontWeight: "bold", letterSpacing: "0.2em", fill: "white" }}>THE</text>
                    <text textAnchor="middle" x="55" y="65" style={{ fontSize: "42px", fill: "white" }}>E.R.</text>
                  </g>

                  <g transform="translate(-20,-40)">
                    <use xlinkHref="#adult" x="5"  y="32" fill={stage3ColorTransition(progress)} />

                    <use xlinkHref="#elder" x="-94"  y="-9" fill={stage3ComproTransition(progress)} />
                    <use xlinkHref="#adult" x="-80"  y="-12" fill={stage3ColorTransition(progress)} />
                    <use xlinkHref="#elder" x="-66"  y="-11" fill={stage3ColorTransition(progress)} />
                    <use xlinkHref="#adult" x="-53"  y="-12" fill={stage3ColorTransition(progress)} />
                    <use xlinkHref="#adult" x="-38"  y="-13" fill={stage3ColorTransition(progress)} />

                    <use xlinkHref="#elder" x="-102"  y="19" fill={stage3ColorTransition(progress)} />
                    <use xlinkHref="#elder" x="-87"  y="17" fill={stage3ColorTransition(progress)} />
                    <use xlinkHref="#adult" x="-72"  y="18" fill={stage3ColorTransition(progress)} />
                    <use xlinkHref="#elder" x="-57"  y="16" fill={stage3ColorTransition(progress)} />
                    <use xlinkHref="#adult" x="-44"  y="18" fill={stage3ColorTransition(progress)} />

                    <use xlinkHref="#child" x="-33"  y="30" fill={stage3ColorTransition(progress)} />

                    <g transform="translate(-110, -29) scale(1.5)">
                      <text className="personal-annotation" y="-24">68 y.o., Army Vet</text>
                      <text className="personal-annotation" y="-12">Chronic Insomnia</text>
                      <text className="personal-annotation immunocompromised-text">Immunocompromised</text>
                      <line x1= "1" y1="2" x2="10" y2="13" className="personal-annotation-line" />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
          <g>
            <Arrow x1={0} y1={21} x2={2} y2={66} />

            <g transform="translate(-4,69)">
              <use xlinkHref="#adult" fill={stage1ColorTransition(progress)} />

              <use x="-14" y="12" xlinkHref="#adult" fill={stage2ColorTransition(progress)} />
              <g transform={`translate(12,14)`}>
                <use xlinkHref="#elder" fill={stage2ColorTransition(progress)} />
              </g>

              <g transform="translate(0, 0)" className="cartoon-annotations" opacity={stage1AnnotationOpacity(progress)}>
                <text x="35" y="23">&hellip;to your aunt.</text>

                <g transform="translate(37, 43)">
                  <text className="personal-annotation" y="10">62 y.o., Aunt</text>
                  <text className="personal-annotation" y="22">Hypertension</text>
                  <line x2="-10" y2="-10" className="personal-annotation-line" />
                </g>
              </g>

              <g className="house-of-grandpa" opacity={stage2AnnotationOpacity(progress)}>
                <Arrow x1={14} y1={30} x2={-2} y2={96} />

                <g transform="translate(27,75) scale(1.5)">
                  <text>&hellip;to grandpa's nursing home.</text>
                </g>

                <g transform="translate(-12,100)">
                  <use xlinkHref="#elder" fill={stage2ColorTransition(progress)} />

                  <use x="-20" xlinkHref="#elder" fill={stage3ComproTransition(progress)} />

                  <g transform="translate(-32, -14) scale(1.5)">
                    <text textAnchor="end" className="personal-annotation" y="-24">89 y.o., Grandpa</text>
                    <text textAnchor="end" className="personal-annotation" y="-12">Hip Transplant</text>
                    <text textAnchor="end" className="personal-annotation immunocompromised-text">Immunocompromised</text>
                    <line x1= "-1" y1="1" x2="8" y2="10" className="personal-annotation-line" />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
      </g>
    </svg>,
    document.getElementById("scrolly-cartoon")
  );
};

export default ScrollyNetwork;